import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '@/utils/supabase';
import { ProspectAnalytics, ProspectFilters } from './types';
import { createComponentLogger } from '@/utils/logger';
import { normalizeSearchQuery } from './search-utils';

const log = createComponentLogger('useServerAnalytics', 'encompass-prospects');

interface ServerAnalyticsResponse {
  totalProspects: number;
  consumerConnectCount: number;
  manualEntryCount: number;
  totalLoanVolume: number;
  averageLoanAmount: number;
  thisMonthCount: number;
  lastMonthCount: number;
  unassignedCount: number;
  assignedCount: number;
  freshCount: number;
  agingCount: number;
  staleCount: number;
  lockedCount: number;
  lockedVolume: number;
  // Segment-bar counts (active Loan Center redesign). Independent of any active segment chip.
  lockExpiringCount: number;
  atRiskCount: number;
  fundedMtdCount: number;
  fundedMtdVolume: number;
}

interface UseServerAnalyticsReturn {
  analytics: ProspectAnalytics;
  isLoading: boolean;
  error: Error | null;
  refetch: () => Promise<void>;
}

const defaultAnalytics: ProspectAnalytics = {
  totalProspects: 0,
  consumerConnectCount: 0,
  manualEntryCount: 0,
  consumerConnectPercentage: 0,
  averageLoanAmount: 0,
  totalLoanVolume: 0,
  lockedCount: 0,
  lockedVolume: 0,
  lockedPercentage: 0,
  thisMonthCount: 0,
  lastMonthCount: 0,
  monthOverMonthChange: 0,
  monthOverMonthPercentage: 0,
  unassignedCount: 0,
  assignedCount: 0,
  freshCount: 0,
  agingCount: 0,
  staleCount: 0,
  lockExpiringCount: 0,
  atRiskCount: 0,
  fundedMtdCount: 0,
  fundedMtdVolume: 0
};

/**
 * Server-side analytics hook that fetches aggregated metrics from database RPC function.
 * Debounces search-only changes by 300ms to match usePaginatedProspects timing.
 * Non-search filter changes fire immediately.
 */
export function useServerAnalytics(
  organizationId: string | undefined,
  filters: ProspectFilters = {},
  includeTransferred: boolean = false,
  enabled: boolean = true
): UseServerAnalyticsReturn {
  const [analytics, setAnalytics] = useState<ProspectAnalytics>(defaultAnalytics);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const searchTimeoutRef = useRef<ReturnType<typeof setTimeout>>();
  const requestIdRef = useRef(0);

  const fetchAnalytics = useCallback(async (currentFilters: ProspectFilters) => {
    if (!organizationId || !enabled) {
      setAnalytics(defaultAnalytics);
      setIsLoading(false);
      return;
    }

    const thisRequestId = ++requestIdRef.current;
    setIsLoading(true);
    setError(null);

    try {
      const timer = log.startTimer('fetch_server_analytics');

      const { data, error: rpcError } = await supabase.rpc('get_prospect_analytics', {
        p_organization_id: organizationId,
        p_loan_folder: currentFilters.loanFolder || null,
        p_include_transferred: includeTransferred,
        p_excluded_folders: currentFilters.excludedFolders?.length ? currentFilters.excludedFolders : null,
        p_search_query: normalizeSearchQuery(currentFilters.searchQuery) || null,
        p_loan_officer_id: currentFilters.loanOfficerId || null,
        p_loan_purpose: currentFilters.loanPurpose || null,
        p_created_after: currentFilters.createdAfter || null,
        p_created_before: currentFilters.createdBefore || null,
        p_min_amount: currentFilters.minAmount || null,
        p_max_amount: currentFilters.maxAmount || null,
        p_excluded_milestones: currentFilters.excludedMilestones?.length ? currentFilters.excludedMilestones : null,
        p_excluded_channels: currentFilters.excludedChannels?.length ? currentFilters.excludedChannels : null,
        p_pipeline: currentFilters.pipeline || null,
        p_lock_status: currentFilters.lockStatus || null,
        p_delivery_status: currentFilters.deliveryStatus || null
      });

      if (rpcError) {
        throw new Error(rpcError.message);
      }

      // RPC returns an array of rows, take the first one
      const serverData = (Array.isArray(data) ? data[0] : data) as ServerAnalyticsResponse;

      if (!serverData) {
        throw new Error('No analytics data returned');
      }

      // Discard stale response — a newer request has been issued
      if (thisRequestId !== requestIdRef.current) {
        log.info('Discarding stale analytics response', { thisRequestId, currentRequestId: requestIdRef.current });
        return;
      }

      // Calculate derived metrics
      const monthOverMonthChange = serverData.thisMonthCount - serverData.lastMonthCount;
      const monthOverMonthPercentage = serverData.lastMonthCount > 0
        ? (monthOverMonthChange / serverData.lastMonthCount) * 100
        : 0;
      const consumerConnectPercentage = serverData.totalProspects > 0
        ? (serverData.consumerConnectCount / serverData.totalProspects) * 100
        : 0;
      const lockedPercentage = serverData.totalProspects > 0
        ? (serverData.lockedCount / serverData.totalProspects) * 100
        : 0;

      const analyticsResult: ProspectAnalytics = {
        totalProspects: serverData.totalProspects,
        consumerConnectCount: serverData.consumerConnectCount,
        manualEntryCount: serverData.manualEntryCount,
        consumerConnectPercentage,
        averageLoanAmount: serverData.averageLoanAmount,
        totalLoanVolume: serverData.totalLoanVolume,
        lockedCount: serverData.lockedCount,
        lockedVolume: serverData.lockedVolume,
        lockedPercentage,
        thisMonthCount: serverData.thisMonthCount,
        lastMonthCount: serverData.lastMonthCount,
        monthOverMonthChange,
        monthOverMonthPercentage,
        unassignedCount: serverData.unassignedCount,
        assignedCount: serverData.assignedCount,
        freshCount: serverData.freshCount,
        agingCount: serverData.agingCount,
        staleCount: serverData.staleCount,
        lockExpiringCount: serverData.lockExpiringCount ?? 0,
        atRiskCount: serverData.atRiskCount ?? 0,
        fundedMtdCount: serverData.fundedMtdCount ?? 0,
        fundedMtdVolume: serverData.fundedMtdVolume ?? 0
      };

      setAnalytics(analyticsResult);
      log.endTimer(timer, { totalProspects: analyticsResult.totalProspects });
    } catch (err) {
      // Discard stale error
      if (thisRequestId !== requestIdRef.current) {
        log.info('Discarding stale analytics error', { thisRequestId, currentRequestId: requestIdRef.current });
        return;
      }
      const error = err instanceof Error ? err : new Error('Failed to fetch analytics');
      log.error('Failed to fetch server analytics', { organizationId, filters: currentFilters }, error);
      setError(error);
    } finally {
      // Only clear loading if this is still the current request
      if (thisRequestId === requestIdRef.current) {
        setIsLoading(false);
      }
    }
  }, [organizationId, includeTransferred, enabled]);

  // Separate search from non-search filters for debounce logic
  const { searchQuery, ...nonSearchFilters } = filters;
  const nonSearchKey = JSON.stringify(nonSearchFilters);
  const prevSearchRef = useRef(searchQuery);

  useEffect(() => {
    // Clear any pending search debounce
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    const searchChanged = prevSearchRef.current !== searchQuery;
    prevSearchRef.current = searchQuery;

    if (searchChanged && searchQuery) {
      // Search-only change: debounce 300ms to match table timing
      searchTimeoutRef.current = setTimeout(() => {
        fetchAnalytics(filters);
      }, 300);
    } else {
      // Non-search filter change or initial load: fire immediately
      fetchAnalytics(filters);
    }

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organizationId, includeTransferred, enabled, searchQuery, nonSearchKey, fetchAnalytics]);

  return {
    analytics,
    isLoading,
    error,
    refetch: () => fetchAnalytics(filters)
  };
}
